/**
 * Renders a large list in batches as the user scrolls.
 * Keeps the full result count while avoiding mounting every item at once.
 */
export function useIncrementalRender<T>(
  source: Ref<T[]>,
  options: { initialCount?: number; batchSize?: number; rootMargin?: string } = {}
) {
  const initialCount = options.initialCount ?? 40;
  const batchSize = options.batchSize ?? 40;
  const rootMargin = options.rootMargin ?? '600px';

  const visibleCount = ref(initialCount);
  const sentinelRef = ref<HTMLElement | null>(null);

  const visibleItems = computed(() => source.value.slice(0, visibleCount.value));
  const hasMore = computed(() => visibleCount.value < source.value.length);

  const loadMore = () => {
    if (!hasMore.value) return;
    visibleCount.value = Math.min(visibleCount.value + batchSize, source.value.length);
  };

  watch(source, () => {
    visibleCount.value = initialCount;
  });

  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin }
    );

    watchEffect((onCleanup) => {
      const element = sentinelRef.value;

      if (!element || !hasMore.value) {
        return;
      }

      observer?.observe(element);

      onCleanup(() => {
        observer?.unobserve(element);
      });
    });
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return {
    visibleItems,
    hasMore,
    sentinelRef,
    loadMore
  };
}
